import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { 
  Plus, 
  Search, 
  Filter,
  Building,
  Phone,
  Mail,
  Globe,
  MapPin,
  Users,
  DollarSign,
  Edit,
  Trash2,
  Eye,
  UserPlus,
  MessageSquare
} from 'lucide-react';
import EmailHistory from './EmailHistory';
import CompanyProfile from './CompanyProfile';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Companies = ({ selectedLocation = 'All Locations' }) => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  // viewingCompany state removed - now using dedicated profile pages
  const [showEmailHistory, setShowEmailHistory] = useState(null);
  
  const { user } = useAuth();

  // Company form state
  const [newCompany, setNewCompany] = useState({
    name: '',
    main_contact_id: 'no-contact',
    status: 'Prospect',
    industry: '',
    website: '',
    address: '',
    phone: '',
    employee_count: '',
    annual_revenue: '',
    notes: '',
    lead_source: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedLocation, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }

      const [companiesData, contactsData] = await Promise.all([
        apiService.getCRMCompanies(filters),
        apiService.getCRMContacts()
      ]);

      setCompanies(companiesData);
      setContacts(contactsData);
    } catch (error) {
      console.error('Error loading companies data:', error);
      setError('Failed to load companies data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async () => {
    if (!newCompany.name) {
      setError('Company name is required');
      return;
    }

    try {
      const companyData = {
        ...newCompany,
        main_contact_id: newCompany.main_contact_id === 'no-contact' ? '' : newCompany.main_contact_id,
        employee_count: newCompany.employee_count ? parseInt(newCompany.employee_count) : null,
        annual_revenue: newCompany.annual_revenue ? parseFloat(newCompany.annual_revenue) : null
      };

      await apiService.createCRMCompany(companyData);
      
      setNewCompany({
        name: '',
        main_contact_id: 'no-contact',
        status: 'Prospect',
        industry: '',
        website: '',
        address: '',
        phone: '',
        employee_count: '',
        annual_revenue: '',
        notes: '',
        lead_source: ''
      });
      setShowAddCompany(false);
      await loadData();
    } catch (error) {
      console.error('Error adding company:', error);
      setError('Failed to add company');
    }
  };

  const handleUpdateCompany = async () => {
    if (!editingCompany) return;

    try {
      const companyData = {
        ...editingCompany,
        employee_count: editingCompany.employee_count ? parseInt(editingCompany.employee_count) : null,
        annual_revenue: editingCompany.annual_revenue ? parseFloat(editingCompany.annual_revenue) : null
      };

      await apiService.updateCRMCompany(editingCompany.id, companyData);
      setEditingCompany(null);
      await loadData();
    } catch (error) {
      console.error('Error updating company:', error);
      setError('Failed to update company');
    }
  };

  const handleStatusChange = async (companyId, newStatus) => {
    try {
      await apiService.updateCRMCompanyStatus(companyId, newStatus);
      await loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update status');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company? This will also remove all associated contacts.')) {
      return;
    }

    try {
      await apiService.deleteCRMCompany(companyId);
      await loadData();
    } catch (error) {
      console.error('Error deleting company:', error);
      setError('Failed to delete company');
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Prospect':
        return 'bg-yellow-100 text-yellow-800';
      case 'Member':
        return 'bg-green-100 text-green-800';
      case 'Former Member':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getMainContactName = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact ? contact.name : 'No Main Contact';
  };

  const getCompanyContacts = (companyId) => {
    return contacts.filter(c => c.company_id === companyId);
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (company.industry && company.industry.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading companies...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Building className="w-8 h-8 mr-3 text-blue-600" />
            Companies
          </h1>
          <p className="text-gray-600 mt-2">Manage your CRM companies and organizations</p>
        </div>
        
        <Dialog open={showAddCompany} onOpenChange={setShowAddCompany}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Company</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Company Name *</Label>
                  <Input
                    id="name"
                    placeholder="Company name"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="main_contact">Main Contact</Label>
                  <Select
                    value={newCompany.main_contact_id}
                    onValueChange={(value) => setNewCompany(prev => ({ ...prev, main_contact_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select main contact (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no-contact">No Main Contact</SelectItem>
                      {contacts.filter(c => !c.company_id || c.company_id === '').map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.name} ({contact.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    placeholder="e.g. Technology, Finance, Healthcare"
                    value={newCompany.industry}
                    onChange={(e) => setNewCompany(prev => ({ ...prev, industry: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    placeholder="https://company.com"
                    value={newCompany.website}
                    onChange={(e) => setNewCompany(prev => ({ ...prev, website: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    placeholder="+1 (555) 123-4567"
                    value={newCompany.phone}
                    onChange={(e) => setNewCompany(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={newCompany.status}
                    onValueChange={(value) => setNewCompany(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Prospect">Prospect</SelectItem>
                      <SelectItem value="Member">Member</SelectItem>
                      <SelectItem value="Former Member">Former Member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  placeholder="Company address"
                  value={newCompany.address}
                  onChange={(e) => setNewCompany(prev => ({ ...prev, address: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="employee_count">Employee Count</Label>
                  <Input
                    id="employee_count"
                    type="number"
                    placeholder="e.g. 50"
                    value={newCompany.employee_count}
                    onChange={(e) => setNewCompany(prev => ({ ...prev, employee_count: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="annual_revenue">Annual Revenue</Label>
                  <Input
                    id="annual_revenue"
                    type="number"
                    placeholder="e.g. 1000000"
                    value={newCompany.annual_revenue}
                    onChange={(e) => setNewCompany(prev => ({ ...prev, annual_revenue: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="lead_source">Lead Source</Label>
                <Input
                  id="lead_source"
                  placeholder="e.g. Website, Referral, Trade Show"
                  value={newCompany.lead_source}
                  onChange={(e) => setNewCompany(prev => ({ ...prev, lead_source: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <textarea
                  id="notes"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Additional notes about the company"
                  value={newCompany.notes}
                  onChange={(e) => setNewCompany(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowAddCompany(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddCompany}>
                  Create Company
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex space-x-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <Input
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Prospect">Prospects</SelectItem>
            <SelectItem value="Member">Members</SelectItem>
            <SelectItem value="Former Member">Former Members</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCompanies.map((company) => {
          const companyContacts = getCompanyContacts(company.id);
          
          return (
            <Card 
              key={company.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/crm/companies/${company.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{company.name}</CardTitle>
                    <p className="text-sm text-gray-600">{company.industry || 'No Industry'}</p>
                  </div>
                  <Badge className={getStatusBadgeColor(company.status)}>
                    {company.status}
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <UserPlus className="w-4 h-4" />
                  <span>{getMainContactName(company.main_contact_id)}</span>
                </div>

                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Users className="w-4 h-4" />
                  <span>{companyContacts.length} contact{companyContacts.length !== 1 ? 's' : ''}</span>
                </div>
                
                {company.phone && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span>{company.phone}</span>
                  </div>
                )}

                {company.website && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Globe className="w-4 h-4" />
                    <a 
                      href={company.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 truncate"
                    >
                      {company.website.replace('https://', '').replace('http://', '')}
                    </a>
                  </div>
                )}

                {company.address && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span className="truncate">{company.address}</span>
                  </div>
                )}

                {company.employee_count && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>{company.employee_count} employees</span>
                  </div>
                )}

                {company.annual_revenue && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <DollarSign className="w-4 h-4" />
                    <span>${company.annual_revenue.toLocaleString()}</span>
                  </div>
                )}

                {company.lead_source && (
                  <div className="text-xs text-gray-500">
                    Source: {company.lead_source}
                  </div>
                )}

                <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                  <div onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={company.status}
                      onValueChange={(value) => handleStatusChange(company.id, value)}
                    >
                      <SelectTrigger className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Prospect">Prospect</SelectItem>
                        <SelectItem value="Member">Member</SelectItem>
                        <SelectItem value="Former Member">Former Member</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEmailHistory(company.id);
                      }}
                      title="View email history"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingCompany(company);
                      }}
                      title="Edit company"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCompany(company.id);
                      }}
                      className="text-red-600 hover:text-red-700"
                      title="Delete company"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredCompanies.length === 0 && (
        <div className="text-center py-12">
          <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || statusFilter !== 'all' 
              ? 'No companies match your current filters' 
              : 'Get started by adding your first company'
            }
          </p>
          <Button onClick={() => setShowAddCompany(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Company
          </Button>
        </div>
      )}

      {/* Company Profile Dialog - Removed: Now using dedicated profile pages */}

      {/* Email History Dialog */}
      {showEmailHistory && (
        <Dialog open={!!showEmailHistory} onOpenChange={() => setShowEmailHistory(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>
                Email History - {companies.find(c => c.id === showEmailHistory)?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="h-[70vh] overflow-y-auto">
              <EmailHistory 
                companyId={showEmailHistory} 
                title={`Email History for ${companies.find(c => c.id === showEmailHistory)?.name}`}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Companies;